// Client « team » du module QUALITÉ — l'annuaire des comptes de la suite.
//
// ⚠️ LES ÉCRITURES PASSENT PAR `fetchEcriture` (U2, 25/08/2026), pas par
// `fetch` nu : le témoin d'attente doit s'allumer ici comme partout ailleurs.
// C'est la première chose qui change en arrivant du site — là-bas ce témoin
// n'existe pas. Sans cela, inviter quelqu'un ne montre rien entre le clic et la
// réponse, et l'on reclique.
//
// ── Déplacé depuis le module Site le 30/08/2026 ────────────────────────────
//
// « Utilisateurs » et « Politique des comptes » se réglaient depuis le site.
// Décision de la RQ : les rapatrier dans le paramétrage de la Qualité. C'est le
// bon endroit — un compte donne accès au système qualité, et c'est la RQ qui
// répond de qui y entre, pas la personne qui édite la vitrine.
//
// ⚠️ LES ROUTES DU MOTEUR N'ONT PAS BOUGÉ : c'est un déplacement de la porte
// d'entrée, pas une réécriture. Proxyées par `/api/team/*` pour que le JWT reste
// dans le cookie httpOnly. Strapi reste l'autorité : il refuse tout ce qui n'est
// pas super admin — cet écran ne fait que le piloter.
import { fetchEcriture } from './ecrire';

export interface AppAccess { site: boolean; manuel: boolean; ged: boolean }

/**
 * Verdict d'envoi de courriel, porté par les actes qui en déclenchent un
 * (invitation, renvoi, validation d'une demande, réinitialisation).
 *
 * ⚠️ Ce n'est PAS redondant avec le succès de l'appel. Ces actes produisent
 * leur effet AVANT l'envoi — compte créé, jeton remplacé, demande marquée
 * approuvée, sessions fermées — et cet effet subsiste quand le message ne part
 * pas. Un écran qui conclurait « envoyé » sur la seule foi du 200 laisserait
 * quelqu'un attendre un courriel qui n'existe pas. L'appelant DOIT distinguer.
 */
export interface VerdictEnvoi {
  /** `false` = l'acte a eu lieu, mais la personne n'a rien reçu. */
  courrielEnvoye: boolean;
  /** motif court et non technique ; `null` si l'envoi a réussi */
  motifEchec: string | null;
}

export interface ResultatReinitialisation extends VerdictEnvoi { ok: boolean; email: string }

/**
 * Lit le verdict d'une réponse serveur.
 *
 * Un serveur ANTÉRIEUR à ce correctif ne renvoie pas le champ. On ne suppose
 * alors pas la réussite : « on ne sait pas » se dit et se traite comme un échec
 * possible — supposer le succès serait exactement le silence qu'on corrige.
 */
export function verdictEnvoi(r: unknown): VerdictEnvoi {
  const o = (r || {}) as { courrielEnvoye?: boolean; motifEchec?: string | null };
  if (o.courrielEnvoye === undefined) return { courrielEnvoye: false, motifEchec: 'le serveur n’a pas confirmé l’envoi' };
  return { courrielEnvoye: o.courrielEnvoye, motifEchec: o.motifEchec ?? null };
}
export interface TeamUser {
  id: number;
  /** le nom à AFFICHER, résolu par le moteur : celui de la fiche RH quand une
   *  personne est rattachée, le libellé du compte sinon (03/10/2026). */
  name: string;
  email: string;
  profile: string;
  /** le CODE du correspondant du carnet relié à ce compte (16/10/2026) ;
   *  `null` = aucun ; absent = serveur antérieur, on n'offre pas la saisie. */
  correspondantCode?: string | null;
  phone?: string;
  apps?: AppAccess;
  username?: string;
  /** clé technique de la personne (orgId) — stable au renommage de l'identifiant */
  cle?: string;
  /** la fiche du personnel reliée à ce compte (lue par la CLÉ, pas par
   *  l'identifiant) ; `null` = aucune fiche ; absent = serveur antérieur au
   *  03/10/2026, l'écran s'abstient alors de nommer qui que ce soit. */
  personne?: { orgId: string; nomAffiche: string; numero: string | null } | null;
  /** le nom de ce compte se corrige-t-il ICI ? Faux dès qu'une personne est
   *  rattachée : il se lit alors sur la fiche et se corrige au dossier RH.
   *  Absent = serveur antérieur au 03/10/2026 → on n'offre pas la saisie. */
  libelleModifiable?: boolean;
  /** la valeur BRUTE du libellé stocké sur le compte (vide = aucun). Distincte
   *  de `name`, qui est le nom résolu et retombe sur l'identifiant. */
  libelle?: string;
  /** cycle de vie du compte : invité · actif · suspendu (absence longue) · archivé (départ) */
  status: 'active' | 'pending' | 'suspended' | 'archived';
  statusSince?: string | null;
  statusReason?: string;
  /** dernière connexion réussie ; `null` = jamais connecté */
  lastLoginAt?: string | null;
  createdAt?: string | null;
  /** échéance du jeton d'invitation (48 h). Présent uniquement sur un compte
   *  encore `pending`. `undefined` = le serveur ne l'expose pas encore : on ne
   *  peut alors PAS distinguer une invitation vivante d'une invitation morte,
   *  et l'écran s'abstient plutôt que de deviner (cf. AVANCEMENT-MAN-6). */
  inviteExpires?: string | null;
}

/**
 * Depuis combien de mois ce compte est-il sans connexion ?
 * Un compte jamais connecté est jugé sur sa date de création : une invitation
 * envoyée hier n'est pas un compte dormant.
 */
export function moisDInactivite(u: TeamUser, maintenant = Date.now()): number | null {
  const ref = u.lastLoginAt || u.createdAt;
  if (!ref) return null;
  const jours = (maintenant - new Date(ref).getTime()) / 86400000;
  return jours / 30.4375;
}

/**
 * Statut DÉTAILLÉ d'un compte — source unique du badge et du filtre.
 *
 * Les six valeurs correspondent à des situations RÉELLEMENT distinctes en base
 * (cf. le chiffrage dans `qa/AVANCEMENT-MAN-6-utilisateurs.md`), pas à des
 * cases inventées :
 *
 *  · `archived`  — `accountStatus='archived'` : départ, connexion fermée.
 *  · `suspended` — `accountStatus='suspended'` : absence longue.
 *  · `expiree`   — `confirmed=0` ET `inviteExpires` dépassé : le lien reçu par
 *                  la personne ne marche plus. Le compte occupe une place et
 *                  n'en donne aucune. Non calculable tant que le serveur
 *                  n'expose pas `inviteExpires` — on retombe alors sur `invite`.
 *  · `invite`    — `confirmed=0`, lien encore valide.
 *  · `jamais`    — compte ACTIF qui ne s'est jamais connecté. Distinct de
 *                  « dormant » : il n'y a pas d'usage à avoir cessé.
 *  · `dormant`   — actif, aucune connexion depuis le seuil paramétré.
 *  · `actif`     — le reste.
 */
export type StatutDetaille = 'actif' | 'invite' | 'expiree' | 'archived' | 'suspended' | 'jamais' | 'dormant';

export function statutDetaille(u: TeamUser, seuilMois: number, maintenant = Date.now()): StatutDetaille {
  if (u.status === 'archived') return 'archived';
  if (u.status === 'suspended') return 'suspended';
  if (u.status === 'pending') {
    if (u.inviteExpires && new Date(u.inviteExpires).getTime() < maintenant) return 'expiree';
    return 'invite';
  }
  if (!u.lastLoginAt) return 'jamais';
  const m = moisDInactivite(u, maintenant);
  if (m !== null && m > seuilMois) return 'dormant';
  return 'actif';
}

/** L'écran ne peut trancher « invitation expirée » que si le serveur donne
 *  l'échéance. Sinon la catégorie est MASQUÉE — afficher « 0 » serait mentir. */
export function expirationConnue(users: TeamUser[]): boolean {
  return users.some((u) => u.inviteExpires !== undefined);
}

async function parse(res: Response) {
  const data = await res.json().catch(() => null);
  if (!res.ok) {
    const msg = data?.error?.message || data?.error || data?.message || 'Erreur serveur';
    throw new Error(typeof msg === 'string' ? msg : 'Erreur serveur');
  }
  return data;
}

export function defaultApps(profile: string): AppAccess {
  const staff = profile === 'superadmin' || profile === 'manager' || profile === 'technician';
  if (staff) return { site: true, manuel: true, ged: true };
  if (profile === 'partner') return { site: true, manuel: true, ged: false };
  return { site: true, manuel: false, ged: false };
}

export async function listUsers(): Promise<TeamUser[]> {
  return parse(await fetch('/api/team/users', { credentials: 'include' }));
}

/** Le compte est créé même si le courriel ne part pas — d'où le verdict. */
export async function inviteUser(email: string, profile: string): Promise<VerdictEnvoi> {
  return verdictEnvoi(await parse(await fetchEcriture('/api/team/invite', {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
    body: JSON.stringify({ email, profile }),
  })));
}

export async function updateUserProfile(id: number, profile: string) {
  return parse(await fetchEcriture(`/api/team/users/${id}`, {
    method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
    body: JSON.stringify({ profile }),
  }));
}

/** Relier (ou délier, code vide) un compte à une ligne du carnet des
 *  correspondants. Le moteur vérifie que le code existe. */
export async function updateUserCorrespondant(id: number, correspondantCode: string) {
  return parse(await fetchEcriture(`/api/team/users/${id}`, {
    method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
    body: JSON.stringify({ correspondantCode }),
  }));
}

/** Le LIBELLÉ d'un compte sans personne (labo correspondant, compte de recette).
 *  Le moteur refuse l'écriture sur un compte rattaché : son nom vient de la
 *  fiche RH. */
export async function updateUserLibelle(id: number, name: string) {
  return parse(await fetchEcriture(`/api/team/users/${id}`, {
    method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
    body: JSON.stringify({ name }),
  }));
}

export async function setUserApps(id: number, apps: AppAccess) {
  return parse(await fetchEcriture(`/api/team/users/${id}/apps`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
    body: JSON.stringify({ apps }),
  }));
}

/**
 * Cycle de vie d'un compte. `suspended` = absence longue (le compte revient
 * tel quel), `archived` = départ de l'établissement. Dans les deux cas la
 * connexion est bloquée mais RIEN n'est supprimé : documents, signatures et
 * journaux gardent leur auteur. Les responsabilités documentaires en cours se
 * transfèrent depuis le module Qualité (organigramme).
 */
export async function setUserStatus(id: number, status: 'active' | 'suspended' | 'archived', reason?: string) {
  return parse(await fetchEcriture(`/api/team/users/${id}/status`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
    body: JSON.stringify({ status, reason }),
  }));
}

export async function deleteUser(id: number) {
  return parse(await fetchEcriture(`/api/team/users/${id}`, { method: 'DELETE', credentials: 'include' }));
}

/**
 * Renvoi d'invitation. Le jeton précédent est REMPLACÉ côté serveur : si le
 * courriel ne part pas, l'ancien lien est mort et le nouveau n'est arrivé nulle
 * part — la personne est dans un état pire qu'avant le clic. D'où le verdict.
 */
export async function resendInvite(id: number): Promise<VerdictEnvoi> {
  return verdictEnvoi(await parse(await fetchEcriture(`/api/team/users/${id}/resend`, { method: 'POST', credentials: 'include' })));
}

/**
 * Réinitialisation du mot de passe — **par lien envoyé à la personne**.
 *
 * AUCUN mot de passe ne transite ici : ni engendré, ni affiché, ni saisi par
 * l'administrateur. Le serveur reprend le mécanisme d'invitation déjà en place
 * (`newInviteToken` : 32 octets, 48 h, champs `inviteToken`/`inviteExpires`) et
 * envoie le lien à l'adresse du compte ; la personne choisit elle-même son mot
 * de passe sur la page publique `/invite/<jeton>`, exactement comme à
 * l'inscription. L'écran d'administration n'apprend jamais le secret, et le
 * compte n'a pas de colonne où un mot de passe en clair pourrait s'écrire.
 *
 * ⚠️ `courrielEnvoye` n'est PAS redondant avec le succès de l'appel. L'acte a
 * lieu — jeton posé, sessions fermées — même quand le courriel ne part pas, et
 * il n'est pas annulable. Un écran qui annoncerait « lien envoyé » sur la seule
 * foi du 200 laisserait une personne dehors sans l'avoir prévenue. L'appelant
 * DOIT distinguer les deux cas.
 */
export async function envoyerLienReinitialisation(id: number): Promise<ResultatReinitialisation> {
  const r = await parse(await fetchEcriture(`/api/team/users/${id}/reset`, { method: 'POST', credentials: 'include' }));
  return { ok: true, email: (r as { email?: string })?.email || '', ...verdictEnvoi(r) };
}

/** Une personne de l'organigramme du module Qualité (BIO24-A-ORG-01). */
export interface PersonneOrg { id: string; nom: string; statut?: string }

/**
 * Personnes de l'organigramme, pour le rapprochement comptes ↔ personnes.
 *
 * Renvoie `null` — et NON une liste vide — quand la lecture échoue : sans
 * cette distinction, un organigramme injoignable ferait apparaître les 14
 * comptes comme « sans personne ». Un écart qui accuse tout le monde n'accuse
 * personne.
 */
export async function listPersonnesOrganigramme(): Promise<PersonneOrg[] | null> {
  try {
    /* ⚠️ La Qualité lit SON organigramme, pas celui proxyé par le site : c'est
       ici qu'il vit. Le chemin change, la sémantique non — `null` veut toujours
       dire « non lu » (droit refusé, module non initialisé), jamais « personne
       à l'organigramme ». Cette nuance porte tout l'écart « compte sans
       personne » : approximer un rapprochement qu'on n'a pas pu faire
       accuserait des comptes légitimes. */
    const res = await fetch('/api/organigramme', { credentials: 'include' });
    if (!res.ok) return null;
    const data = await res.json();
    return Array.isArray(data?.personnes) ? (data.personnes as PersonneOrg[]) : null;
  } catch {
    return null;
  }
}

/**
 * LES DEMANDES D'INSCRIPTION — UNE SEULE PORTE, ICI.
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Jusqu'au 13/09/2026, ces trois gestes existaient DEUX fois : ici, et dans
 * l'inbox « Demandes reçues » du site (`site/src/lib/inbox.ts`). Deux écrans,
 * deux boutons « Valider », un même serveur : rien n'empêchait deux personnes
 * de trancher la même demande, et la seconde trouvait un état déjà changé sans
 * savoir par qui ni quand.
 *
 * Arbitrage de la RQ : **la Qualité répond et consulte, un seul endroit.**
 *
 * ⚠️ ET LE SITE NE GARDE PLUS RIEN DU TOUT depuis le 05/10/2026. Le 13/09 on
 * lui avait laissé les rendez-vous, qu'il est seul à produire ; Inès a tranché
 * le reste — l'écran « Demandes reçues », son module de données
 * (`site/src/lib/inbox.ts`) et son proxy ont été SUPPRIMÉS. Le site dépose les
 * demandes et prévient par courriel, il n'en traite aucune. Les rendez-vous se
 * confirment dans `ged/src/components/screens/DemandesRdv.tsx`.
 *
 * ⚠️ `id` EST UNE CHAÎNE, malgré son nom. Le moteur renvoie ici le
 * `documentId` de Strapi (« abc123xyz… »), pas la clé numérique. Le champ était
 * typé `number` et ne servait qu'à être recollé dans une URL, ce qui marchait
 * par accident : `${id}` rend la même chose des deux côtés. Le type disait donc
 * une chose fausse sans jamais échouer — exactement la forme de mensonge que
 * `tsc` ne peut pas voir.
 */
export interface AccessRequest {
  id: string;
  /** Référence lisible (« BIO24-CPT-1234 ») : c'est par elle que le SUIVI et le
   *  JOURNAL d'une demande se retrouvent (`cibleRef`, `ficheCode`). */
  ref?: string;
  prenom: string;
  nom: string;
  institution: string;
  email: string;
  phone?: string;
  city?: string;
  country?: string;
  /** 'partner' depuis le 16/10/2026 ; 'staff' ne subsiste que sur d'anciennes
   *  demandes — le personnel reçoit son compte depuis son dossier RH. */
  role: string;
  /** Code du type de correspondant déclaré (carnet) et son libellé. */
  typeCorrespondant?: string;
  typeLibelle?: string;
  /** Fonction de la personne au sein de son institution. */
  fonction?: string;
  /** Ligne du carnet à laquelle le compte a été relié à la validation. */
  correspondantCode?: string;
  status: 'pending' | 'approved' | 'rejected';
  /** Date déjà formatée pour l'affichage (« 28/07/2026 »). */
  date?: string;
  /** Date brute, pour comparer — « 28/07/2026 » ne se compare pas. */
  createdAt?: string;
}

export async function listRequests(): Promise<AccessRequest[]> {
  return parse(await fetch('/api/team/requests', { credentials: 'include' }));
}
/**
 * Le lien au carnet, décidé par la RQ (16/10/2026) : la ligne existante par son
 * code, OU la création depuis la demande. L'un des deux est obligatoire — le
 * moteur refuse un compte Correspondant qui ne serait relié à rien.
 */
export type LienCarnet = { correspondantCode: string } | { creerCorrespondant: true };

export type VerdictApprobation = VerdictEnvoi & { correspondantCode: string; correspondantCree: boolean };

export async function approveRequest(id: string, profile: string, lien: LienCarnet): Promise<VerdictApprobation> {
  const r = await parse(await fetchEcriture(`/api/team/requests/${id}/approve`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
    body: JSON.stringify({ profile, ...lien }),
  })) as { correspondantCode?: string; correspondantCree?: boolean };
  return { ...verdictEnvoi(r), correspondantCode: String(r?.correspondantCode || ''), correspondantCree: r?.correspondantCree === true };
}
/**
 * Refuse une demande — ET PRÉVIENT LE DEMANDEUR (13/09/2026).
 *
 * Le refus était muet jusque-là : le serveur changeait le statut et rendait
 * `{ ok: true }`, la personne n'apprenait rien. Elle reçoit désormais un
 * message, et ce geste porte donc un verdict d'envoi comme l'approbation —
 * même règle : le refus tient même si le courriel ne part pas, et c'est
 * l'écran qui doit dire les deux faits.
 */
export async function rejectRequest(id: string): Promise<VerdictEnvoi> {
  return verdictEnvoi(await parse(await fetchEcriture(`/api/team/requests/${id}/reject`, { method: 'POST', credentials: 'include' })));
}
/** Remet une demande REFUSÉE en attente. Une demande validée, elle, a déjà créé
 *  un compte : la « remettre en attente » ne le défait pas — le serveur ne
 *  propose donc ce geste que sur un refus. */
export async function reopenRequest(id: string) {
  return parse(await fetchEcriture(`/api/team/requests/${id}/reopen`, { method: 'POST', credentials: 'include' }));
}
